// @flow
import { createSelector } from 'reselect';

import {
  selectIsLoadedInfos as selectIsLoadedCurrency,
  selectCurrency,
} from '@gandi/billing';
import { selectOneOrganization } from '@gandi/redux-organization';
import {
  selectIsMandatorySubdivision,
  selectIsMandatoryZipCode,
} from '@gandi/redux-places';
import {
  selectCurrent,
  selectCurrentId,
  type Organization,
} from '@gandi/redux-sharing';

import type { OrganizationShape } from 'src/hooks/useMissingOrgInfo';
import type { FullState } from 'src/types';

const orgFields = [
  'type',
  'firstname',
  'lastname',
  'country',
  'city',
  'email',
  'bu_id',
];

declare function selectShopCurrent(
  state: FullState,
  /** WARN: should be false while changing the cart owner */
  isAuthenticated: true,
): Organization;
// eslint-disable-next-line no-redeclare
declare function selectShopCurrent(
  state: FullState,
  /** WARN: should be false while changing the cart owner */
  isAuthenticated?: false,
): void | Organization;

/* If user is authenticated then selectCurrent returns an organization, but it may not be true when he is not */
// eslint-disable-next-line no-redeclare
export function selectShopCurrent(state: FullState, isAuthenticated: boolean) {
  const currentOrg =
    selectCurrent(state) ||
    // selectCurrent from redux-sharing should always be filled when authenticated
    // however when user switch orga through the preferences dialog
    // selectCurrent does not find the freshly selected organization
    // in this case, the organization should be find from redux-organization instead of redux-sharing
    selectOneOrganization(state, selectCurrentId(state));

  return isAuthenticated ? currentOrg : currentOrg || undefined;
}

export const selectCurrentBU = (state: FullState): void | number =>
  selectShopCurrent(state, false)?.bu_id;
/**
 * We need to check that all of the target organization's info is provided
 * before we use this info to generate domain contacts, allow payment, etc...
 * Return the list of missing fields if any.
 * @private
 */
interface OrgInfoParams {
  orgId?: string;
  orga?: ?OrganizationShape;
}

type MissingOrgFields = Array<string>;

const ORG_FIELDS_TO_CHECK = orgFields;

/**
 * Checks for missing basic fields directly on the org object
 */
const getBaseMissingFields = (org: OrganizationShape): MissingOrgFields => {
  // $FlowFixMe[prop-missing]
  const missingFields = ORG_FIELDS_TO_CHECK.filter(field => !org[field]);

  if (org.type && org.type !== 'individual' && !org.orgname) {
    missingFields.push('orgname');
  }

  return missingFields;
};

/**
 * Check for missing geographic fields
 */
const selectMissingGeographicFields = (
  state: FullState,
  org: OrganizationShape,
): MissingOrgFields => {
  const missingFields: MissingOrgFields = [];

  if (!org.country) {
    return missingFields;
  }

  const mandatorySubdivision = selectIsMandatorySubdivision(state, org.country);
  const mandatoryZipCode = selectIsMandatoryZipCode(state, org.country);

  if (mandatorySubdivision && !org.state) {
    missingFields.push('state');
  }

  if (mandatoryZipCode && !org.zip) {
    missingFields.push('zip');
  }

  return missingFields;
};

/**
 * Check if the currency is missing.
 */
const selectMissingCurrency = (state: FullState, orgId: string): boolean => {
  const currencyProps = { sharingId: orgId };
  const loadedCurrency = selectIsLoadedCurrency(state, currencyProps);
  const currency = selectCurrency(state, currencyProps);

  return loadedCurrency && !currency;
};

/**
 * It uses the currentId id by default.
 */
const findOrganizationInState = (
  state: FullState,
  orgId?: string,
): ?OrganizationShape | null => {
  const currentId = orgId || selectCurrentId(state);
  if (!currentId) {
    return null;
  }

  // Todo: Why we use redux-sharing and redux-organization. => we can use that redux-organization.
  return (
    selectOneOrganization(state, currentId) || selectShopCurrent(state, false)
  );
};

/**
 * Find the missing fields for a given organization
 */
const selectMissingFields = (
  state: FullState,
  org: OrganizationShape,
): MissingOrgFields => {
  const missingFields: MissingOrgFields = [
    ...getBaseMissingFields(org),
    ...selectMissingGeographicFields(state, org),
  ];

  if (selectMissingCurrency(state, org.id)) {
    missingFields.push('currency');
  }

  return missingFields;
};

/**
 * Main selector that combines search and check
 */
export const selectMissingOrgInfo: (
  FullState,
  ?OrgInfoParams,
) => MissingOrgFields | void = createSelector(
  [
    (state, args) => args?.orga || findOrganizationInState(state, args?.orgId),
    state => state,
  ],
  (org, state): MissingOrgFields | void => {
    if (!org) {
      return undefined;
    }
    const missingFields = selectMissingFields(state, org);
    return missingFields.length > 0 ? missingFields : undefined;
  },
);

/**
 * Check if the organization has any missing information.
 */
export const selectHasMissingOrgInfo: (
  FullState,
  ?OrgInfoParams,
) => boolean | void = createSelector(
  [selectMissingOrgInfo],
  (missingOrgInfo): boolean | void => {
    if (missingOrgInfo === undefined) {
      return undefined;
    }

    return missingOrgInfo.length > 0;
  },
);
